import { DatabaseModel } from './database-model';
import { DistributionChannel, DistributionChannelString } from './distribution-channel';
import { PartnerCostTransferType, PartnerCostType, Product } from './product';

export class ProductVoucher {
    product: Product;
    voucher: Voucher;
}

export class Voucher extends DatabaseModel {
    public key: string;
    // User key
    public userKey: string = '';
    // The unique key of the visitor (from web / app cookie)
    public visitorKey: string = '';
    // Product key
    public productKey: string = '';
    // Created on date
    public createdOn: Date | any = null;
    // User claimed the voucher on this date
    public claimedOn: Date | any = null;
    // Set when voucher was claimed through PTG flow
    public claimedInPtg: boolean = false;
    // Redeemed date (redeemed by shop/staff on this date)
    public redeemedOn: Date | any = null;
    // Refund
    public refundedOn: Date | any = null;
    public refundedReason: string = '';
    // Set when voucher was manually migrated (triggers backend processing)
    public manuallyMigratedOn: Date | any = null;
    // First unlocked mote date
    public tourStartedOn: Date | any = null;
    // Stripe payment id reference
    public paymentId: string;
    // User email
    public email: string = '';
    // Public any comments (regarding free vouchers or other)
    public comments: string = '';
    // Price for voucher as per time of purchase (in øre)
    public price: number = 0;
    // Quiz points that enabled the user to claim this voucher/reward 
    public quizPoints: number = 0;
    // Partner cost as per purchase (in øre)
    public partnerCost: number = null;
    // The partnerCostTransferType from the project at the time of purchasing the voucher
    public partnerCostTransferType: PartnerCostTransferType = PartnerCostTransferType.StripeTransfer;
    // Parent voucher key
    public parent: string = '';
    // Pool key shared by all vouchers purchased together
    public poolKey: string = '';
    // The channel behind this voucher purchase
    public distributionChannel: DistributionChannelString = DistributionChannel.StoryHuntHome;
    // Optional barcode that is shown on addon vouchers
    public barcode: string = '';
    // Code to claim this voucher (this can be a get your guide key ie. "GYG2RAX4WX8B" or a StoryHunt claim code ie. "abc123" )
    claimCode: string = '';

    public isFreeTicket: boolean = false;

    constructor(json: any = null, key: string = '') {
        super();
        this.fromJson(json);
        this.key = key ? key : this.genKey();
    }

    public toJson() {
        return {
            key: this.key,
            userKey: this.userKey,
            visitorKey: this.visitorKey,
            productKey: this.productKey,
            redeemedOn: this.redeemedOn,
            claimedOn: this.claimedOn,
            claimedInPtg: this.claimedInPtg,
            createdOn: this.createdOn,
            refundedOn: this.refundedOn,
            refundedReason: this.refundedReason,
            manuallyMigratedOn: this.manuallyMigratedOn,
            tourStartedOn: this.tourStartedOn,
            paymentId: this.paymentId,
            isFreeTicket: this.isFreeTicket,
            email: this.email,
            comments: this.comments,
            price: this.price,
            quizPoints: this.quizPoints,
            partnerCost: this.partnerCost,
            partnerCostTransferType: this.partnerCostTransferType,
            parent: this.parent,
            poolKey: this.poolKey,
            distributionChannel: this.distributionChannel,
            barcode: this.barcode,
            claimCode: this.claimCode,
        }
    }

    public fromJson(json: any): Voucher {
        if(!json) return this;

        this.userKey = json.userKey || "";
        this.visitorKey = json.visitorKey || "";
        this.productKey = json.productKey || "";
        this.redeemedOn = json.redeemedOn || "";
        this.claimedOn = json.claimedOn || "";
        this.claimedInPtg = json.claimedInPtg || false;
        this.createdOn = json.createdOn ? new Date(json.createdOn) : null;
        this.refundedOn = json.refundedOn ? new Date(json.refundedOn) : null,
        this.refundedReason = json.refundedReason || "",
        this.manuallyMigratedOn = json.manuallyMigratedOn ? new Date(json.manuallyMigratedOn) : null,
        this.tourStartedOn = json.tourStartedOn || "";
        this.paymentId = json.paymentId || "";
        this.isFreeTicket = json.isFreeTicket || false;
        this.email = json.email || "";
        this.comments = json.comments || "";
        this.price = json.price || 0;
        this.quizPoints = json.quizPoints || 0;
        this.partnerCost = json.partnerCost || "";
        this.partnerCostTransferType = json.partnerCostTransferType || PartnerCostTransferType.StripeTransfer;
        this.parent = json.parent || "";
        this.poolKey = json.poolKey || "";
        this.distributionChannel = json.distributionChannel || DistributionChannel.StoryHuntHome;
        this.barcode = json.barcode || "";
        this.claimCode = json.claimCode || "";
        return this;
    }

    /**
     * @param voucherPrice The purchase price (OBS: in øre) of a single voucher (if child, this is the parent price) - discount has been accounted for at checkout
     * @param product The product belonging to that voucher
     * @returns The voucher partner cost (OBS: in øre): Either a proportion of the price or a fixed amount. Both numbers are saved in the product property 'partnerCost'
     */
    public static getPartnerCost(voucherPrice: number, product: Product): number {
        const partnerCost = (product.partnerCostType == PartnerCostType.PERCENTAGE) ? (voucherPrice * (product.partnerCost / 100)) : product.partnerCost;
        return Math.round(partnerCost || 0);
    }
}
